import { useEffect, useMemo, useRef, useState } from "react";
import type { GraphyClient, Scope } from "../api/client";
import { ApiError } from "../api/client";
import type { McpResult, McpTool } from "../api/types";
import { ErrorState, Loading } from "../components/states";
import { T } from "../i18n";

// GY-070 MCP tool explorer: schema-based inputs, execute/cancel, the actual
// structured response, copy/export, and source navigation. The result discloses
// truncation, missing capability (as a typed error), and provenance
// (analysisKind). Results are graph structure only — no tokens/secrets ever
// appear in the payload viewer (the backend never puts them there).

interface Props {
  client: GraphyClient;
  scope: Scope;
  labelOf: (id: string) => string;
  onViewSource: (id: string) => void;
}

export function McpExplorer({ client, scope, labelOf, onViewSource }: Props) {
  const [tools, setTools] = useState<McpTool[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [toolName, setToolName] = useState<string>("");
  const [params, setParams] = useState<Record<string, string>>({});
  const [result, setResult] = useState<McpResult | null>(null);
  const [running, setRunning] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let live = true;
    client
      .mcpTools({ projectId: scope.projectId })
      .then((t) => {
        if (!live) return;
        setTools(t);
        if (t[0]) selectTool(t[0]);
      })
      .catch((e) => {
        if (e instanceof ApiError || e instanceof Error) setLoadError(true);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, scope.projectId]);

  const tool = useMemo(() => tools?.find((t) => t.name === toolName) ?? null, [tools, toolName]);

  function selectTool(t: McpTool) {
    setToolName(t.name);
    const defaults: Record<string, string> = {};
    for (const [name, schema] of Object.entries(t.params)) {
      if (schema.default !== undefined) defaults[name] = String(schema.default);
    }
    setParams(defaults);
    setResult(null);
    setCancelled(false);
  }

  async function execute() {
    if (!tool) return;
    setRunning(true);
    setCancelled(false);
    setResult(null);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await client.mcpInvoke(
        { projectId: scope.projectId, snapshotId: scope.snapshotId, tool: tool.name, params },
        ctrl.signal,
      );
      setResult(res);
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") setCancelled(true);
      else setResult({
        requestId: "", status: "error", tool: tool.name, scope: {}, data: null,
        analysisKind: "", coverage: null, limitations: [],
        error: { code: "service_failure", message: T.common.error }, generatedAt: "", schemaVersion: 1,
      });
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  }

  function cancel() {
    abortRef.current?.abort();
  }

  async function copy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  }

  function exportJson() {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${result.tool}-${result.requestId || "result"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loadError) return <ErrorState />;
  if (!tools) return <Loading />;
  if (tools.length === 0) return <div className="state state-empty">{T.mcp.noTools}</div>;

  const nodeIds = result?.data ? collectNodeIds(result.data) : [];

  return (
    <div className="mcp">
      <div className="mcp-controls">
        <label>
          {T.mcp.pick}
          <select aria-label={T.mcp.pick} value={toolName} onChange={(e) => {
            const t = tools.find((x) => x.name === e.target.value);
            if (t) selectTool(t);
          }}>
            {tools.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {tool && (
        <form
          className="mcp-form"
          onSubmit={(e) => {
            e.preventDefault();
            void execute();
          }}
        >
          {Object.entries(tool.params).map(([name, schema]) => {
            const req = tool.required.includes(name);
            const value = params[name] ?? "";
            const set = (v: string) => setParams((prev) => ({ ...prev, [name]: v }));
            return (
              <label key={name}>
                {name}
                {req ? ` (${T.mcp.required})` : ""}
                {schema.type === "enum" ? (
                  <select aria-label={name} value={value} onChange={(e) => set(e.target.value)}>
                    {(schema.values ?? []).map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                ) : schema.type === "boolean" ? (
                  <input type="checkbox" aria-label={name} checked={value === "true"} onChange={(e) => set(String(e.target.checked))} />
                ) : (
                  <input
                    type={schema.type === "integer" ? "number" : "text"}
                    aria-label={name}
                    value={value}
                    min={schema.min}
                    max={schema.max}
                    required={req}
                    onChange={(e) => set(e.target.value)}
                  />
                )}
              </label>
            );
          })}
          <div className="mcp-actions">
            {running ? (
              <button type="button" className="btn" onClick={cancel}>
                {T.mcp.cancel}
              </button>
            ) : (
              <button type="submit" className="btn selected-tab">
                {T.mcp.execute}
              </button>
            )}
          </div>
        </form>
      )}

      {cancelled && <div className="banner banner-partial">{T.mcp.cancelled}</div>}
      {running && <Loading />}

      {result && !running && (
        <div className="mcp-result">
          <div className="mcp-result-head">
            <span className="tag">{result.status}</span>
            <span className="tag">
              {T.mcp.provenance}: {result.analysisKind || "—"}
            </span>
            <div className="mcp-result-actions">
              <button className="btn" onClick={copy}>
                {copied ? T.mcp.copied : T.mcp.copy}
              </button>
              <button className="btn" onClick={exportJson}>
                {T.mcp.exportJson}
              </button>
            </div>
          </div>

          {result.error && (
            <div className="banner banner-error" role="alert">
              {result.error.code === "unsupported" ? T.mcp.missingCapability : result.error.message}
            </div>
          )}
          {result.coverage?.truncated && <div className="banner banner-partial">{T.mcp.truncated}</div>}
          {result.limitations.map((lim, i) => (
            <div key={i} className="muted mcp-limitation">
              {lim}
            </div>
          ))}

          {nodeIds.length > 0 && (
            <div className="mcp-nodes">
              {nodeIds.map((id) => (
                <button key={id} className="link" onClick={() => onViewSource(id)} title={T.mcp.openSource}>
                  {labelOf(id)}
                </button>
              ))}
            </div>
          )}

          <pre className="code" aria-label={T.mcp.result}>
            <code>{JSON.stringify(result.data, null, 2)}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

// Pull symbol ids out of a result payload so the explorer can link to source.
// Only strings that look like namespaced symbol ids (contain "::") are offered.
function collectNodeIds(data: Record<string, unknown>): string[] {
  const ids = new Set<string>();
  const add = (v: unknown) => {
    if (typeof v === "string" && v.includes("::")) ids.add(v);
  };
  for (const v of Object.values(data)) {
    if (typeof v === "string") add(v);
    else if (Array.isArray(v)) {
      for (const item of v) {
        if (typeof item === "string") add(item);
        else if (Array.isArray(item)) item.forEach(add);
        else if (item && typeof item === "object") {
          const o = item as Record<string, unknown>;
          add(o.node);
          if (Array.isArray(o.members)) o.members.forEach(add);
        }
      }
    }
  }
  return [...ids];
}
